import { Big } from 'big.js';
import {
    bigToString,
    Corridor,
    EnumCorridorColor,
    parseBig,
    QuoteService,
    Tools
} from 'quote-domain-core';
import { CorridorService } from '../corridor/corridor-service';
import { PricingItemCorridorService } from '../pricing-item/pricing-item-corridor-service';
import { PricingItemPriceService } from '../pricing-item/pricing-item-price-service';
import { singleton } from 'tsyringe';

export type QuoteServiceAppliedPriceSummary = {
    appliedPriceDevices: Big;
    appliedPriceConsumables: Big;
    appliedPriceOnlyNegociableDevices: Big;
    appliedPriceOnlyNegociableConsumables: Big;
    appliedPriceOnlyNonNegociableDevices: Big;
    appliedPriceOnlyNonNegociableConsumables: Big;
};

@singleton()
export class QuoteServiceCorridorAggregationService {
    constructor(
        private readonly corridorService: CorridorService,
        private readonly pricingItemCorridorService: PricingItemCorridorService,
        private readonly pricingItemPriceService: PricingItemPriceService
    ) {
    }

    public sumDevicesAndConsumablesAppliedPrices(quoteService: QuoteService): QuoteServiceAppliedPriceSummary {
        const result: QuoteServiceAppliedPriceSummary = {
            appliedPriceDevices: new Big(0),
            appliedPriceConsumables: new Big(0),
            appliedPriceOnlyNegociableDevices: new Big(0),
            appliedPriceOnlyNegociableConsumables: new Big(0),
            appliedPriceOnlyNonNegociableDevices: new Big(0),
            appliedPriceOnlyNonNegociableConsumables: new Big(0)
        };

        (quoteService.customGroups ?? []).forEach(customGroup => {
            (customGroup.pricingItems ?? []).forEach(pricingItem => {
                this.pricingItemPriceService.sumDevicesAndConsumablesAppliedPrices(pricingItem, result);
            });
        });

        return result;
    }

    public getJoinCorridorNegotiable(quoteService: QuoteService): void {
        quoteService.corridorConsumables = new Corridor();
        quoteService.corridorDevices = new Corridor();
        quoteService.corridorConsumablesOnlyNegociable = new Corridor();
        quoteService.corridorDevicesOnlyNegociable = new Corridor();

        let allNeutral = true;
        (quoteService.customGroups ?? []).forEach(customGroup => {
            (customGroup.pricingItems ?? []).forEach(pricingItem => {
                this.corridorService.addCorridor(quoteService.corridorConsumables, this.pricingItemCorridorService.getCorridorConsumables(pricingItem, true, true));
                this.corridorService.addCorridor(quoteService.corridorConsumablesOnlyNegociable, this.pricingItemCorridorService.getCorridorConsumables(pricingItem, true, false));
                this.corridorService.addCorridor(quoteService.corridorDevices, this.pricingItemCorridorService.getCorridorDevices(pricingItem, true, true));
                this.corridorService.addCorridor(quoteService.corridorDevices, this.pricingItemCorridorService.getCorridorOptions(pricingItem, true, true));
                this.corridorService.addCorridor(quoteService.corridorDevicesOnlyNegociable, this.pricingItemCorridorService.getCorridorDevices(pricingItem, true, false));
                this.corridorService.addCorridor(quoteService.corridorDevicesOnlyNegociable, this.pricingItemCorridorService.getCorridorOptions(pricingItem, true, false));
                if (!this.pricingItemPriceService.isNeutralCorridor(pricingItem)) {
                    allNeutral = false;
                }
            });
        });

        quoteService.globalCorridor = new Corridor();
        quoteService.globalCorridor.isNeutral = allNeutral;
        if (quoteService.corridorConsumables) {
            this.corridorService.addCorridor(quoteService.globalCorridor, quoteService.corridorConsumables);
        }
        if (quoteService.corridorDevices) {
            this.corridorService.addCorridor(quoteService.globalCorridor, quoteService.corridorDevices);
        }

        quoteService.globalCorridorOnlyNegociable = new Corridor();
        if (quoteService.corridorConsumablesOnlyNegociable) {
            this.corridorService.addCorridor(quoteService.globalCorridorOnlyNegociable, quoteService.corridorConsumablesOnlyNegociable);
        }
        if (quoteService.corridorDevicesOnlyNegociable) {
            this.corridorService.addCorridor(quoteService.globalCorridorOnlyNegociable, quoteService.corridorDevicesOnlyNegociable);
        }
    }

    public updateQuoteServiceDiscount(quoteService: QuoteService, appliedPriceDevices: Big, appliedPriceConsumables: Big): void {
        const appliedPrice = appliedPriceDevices.add(appliedPriceConsumables);
        this.getJoinCorridorNegotiable(quoteService);

        let discount = null;
        let devicesDiscount = null;
        let consumablesDiscount = null;

        if (quoteService.globalCorridor) {
            discount = Tools.getDiscount(appliedPrice, parseBig(this.corridorService.getHighestLevel(quoteService.globalCorridor)?.min));
        }
        if (quoteService.corridorDevices) {
            devicesDiscount = Tools.getDiscount(appliedPriceDevices, parseBig(this.corridorService.getHighestLevel(quoteService.corridorDevices)?.min));
        }
        if (quoteService.corridorConsumables) {
            consumablesDiscount = Tools.getDiscount(appliedPriceConsumables, parseBig(this.corridorService.getHighestLevel(quoteService.corridorConsumables)?.min));
        }

        Object.assign(quoteService, {
            discount: bigToString(discount),
            devicesDiscount: bigToString(devicesDiscount),
            consumablesDiscount: bigToString(consumablesDiscount)
        });
    }

    public updateServiceDistanceFromAppliedPrice(quoteService: QuoteService): void {
        if (!!quoteService.globalCorridorOnlyNegociable && !!quoteService.globalCorridorOnlyNegociable.levels && quoteService.globalCorridorOnlyNegociable.levels.size > 0) {
            let distance = new Big('1');
            let color = EnumCorridorColor.GREEN.valueOf();
            if (parseBig(this.corridorService.getHighestLevel(quoteService.globalCorridorOnlyNegociable).min).gt(0)) {
                distance = this.corridorService.getDistanceByAppliedPrice(quoteService.globalCorridorOnlyNegociable, quoteService.appliedPriceOnlyNegociable);
                color = this.corridorService.getMatchedColorByappliedPrice(quoteService.globalCorridorOnlyNegociable, quoteService.appliedPriceOnlyNegociable, quoteService.nonNegociableArticleColor);
            }
            Object.assign(quoteService, { distance: bigToString(distance), appliedPriceColor: color });
        }
    }
}

